import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { IMG } from "@/lib/assets";

const EASE = [0.16, 1, 0.3, 1];

export const Hero = ({ onCta }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const scaleImg = useTransform(scrollYProgress, [0, 1], [1.05, 1.15]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative w-full min-h-screen overflow-hidden">
      {/* Full-bleed background photo */}
      <motion.img
        style={{ scale: scaleImg }}
        src={IMG.heroWide}
        alt="On the water"
        className="absolute inset-0 h-full w-full object-cover object-center"
      />
      {/* Legibility scrim */}
      <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-ink/45 to-ink/25" />

      {/* Copy over the photo, spanning the full page */}
      <div className="relative z-10 flex min-h-screen w-full flex-col justify-end px-6 pb-20 pt-32 md:px-12 md:pb-28">
        <motion.p
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: EASE, delay: 0.1 }}
          className="text-xs sm:text-sm tracking-[0.34em] uppercase font-semibold text-cream/80 mb-5"
        >
          A Complimentary Guided Meditation
        </motion.p>

        <motion.h1
          initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.95, ease: EASE, delay: 0.22 }}
          className="font-display font-black leading-[0.95] tracking-tight text-cream text-[2.6rem] sm:text-6xl lg:text-[6.2vw] lg:whitespace-nowrap"
        >
          Give Your Energy An Upgrade...
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: EASE, delay: 0.5 }}
          className="mt-6 font-display italic font-medium text-2xl md:text-3xl text-cream"
        >
          A cellular upgrade delivered through sound.
        </motion.p>
        <motion.p
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: EASE, delay: 0.6 }}
          className="mt-5 max-w-xl text-lg md:text-xl font-light leading-relaxed text-cream/85"
        >
          Created for the woman who produces at a high level and wants to feel like a million bucks from the inside out.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: EASE, delay: 0.68 }}
          className="mt-9 flex justify-center"
        >
          <button
            data-testid="hero-cta-button"
            onClick={onCta}
            className="group inline-flex items-center bg-rose text-cream text-sm font-semibold uppercase tracking-[0.2em] px-9 py-4 shadow-[0_16px_40px_-16px_rgba(0,0,0,0.6)] transition-colors duration-300 hover:bg-rose-hover active:scale-[0.98]"
          >
            Send me the meditation
          </button>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
